import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import { PosContext } from "../../shared/PosContext";
import { getTakeawayUrlSegment } from "../../utils/floorUtils";
import { useCallCenterDraftOrders } from "../../shared/hooks/useCallCenterOrdersData";
import { usePrintOrder } from "../../shared/hooks/useTablesData";
import { dateTimeFormat } from "../../utils/dateUtils";
import { getAlertRemaining } from "../../utils/draftAlertUtils";
import { getTableOrderPrintFormat } from "../../utils/printFormatUrl";

const CallCenterOrdersDrawer = ({ open, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const printOrder = usePrintOrder();
    const { posProfile } = useContext(AppContext);
    const { takeawayTable } = useContext(PosContext);
    const tableOrderPrintFormat = getTableOrderPrintFormat(posProfile);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const alertedDraftsRef = useRef(new Set());

    const takeawayPathSegment = useMemo(
        () => getTakeawayUrlSegment(posProfile, takeawayTable),
        [posProfile, takeawayTable]
    );

    const {
        data: orders,
        isLoading,
        refetch,
        callCenterUsersLoading,
    } = useCallCenterDraftOrders();

    const list = useMemo(() => {
        return Array.isArray(orders) ? orders : [];
    }, [orders]);

    useEffect(() => {
        if (!open) return undefined;
        setNowMs(Date.now());
        const id = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(id);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        list.forEach((order) => {
            const alertKey = `${order?.name || ""}:${order?.alert_time || ""}`;
            const remaining = getAlertRemaining(order?.alert_time, nowMs);
            if (!order?.alert_time || !remaining.isDue) return;
            if (alertedDraftsRef.current.has(alertKey)) return;
            alertedDraftsRef.current.add(alertKey);
            enqueueSnackbar(
                t("draftAlerts.snackbar", {
                    order: order?.name || "",
                    defaultValue: "Draft {{order}} is due",
                }),
                { variant: "warning" }
            );
        });
    }, [list, nowMs, open, t]);

    const handleOpenOrder = (orderName) => {
        if (!orderName) return;
        onClose?.();
        navigate(`/pos/${takeawayPathSegment}/${orderName}`);
    };

    const handlePrintOrder = (orderName) => {
        if (!orderName) return;
        printOrder.mutate({
            orderId: orderName,
            doctype: "Table Order",
            printFormat: tableOrderPrintFormat,
        });
    };

    return (
        <Drawer anchor="right" open={!!open} onClose={onClose}>
            <Box sx={{ width: 420, p: 2 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                >
                    <Typography variant="h6">
                        {t("callCenterOrders.title")}
                    </Typography>
                    <Button variant="outlined" onClick={() => refetch?.()}>
                        {t("common.refresh")}
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" mb={2}>
                    {t("callCenterOrders.subtitle")}
                </Typography>

                {(isLoading || callCenterUsersLoading) && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={3}
                        gap={1}
                    >
                        <CircularProgress size={20} />
                        <Typography variant="body2">
                            {t("common.loading")}
                        </Typography>
                    </Box>
                )}

                {!isLoading && !callCenterUsersLoading && list.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {t("callCenterOrders.empty")}
                    </Typography>
                )}

                {!isLoading &&
                    !callCenterUsersLoading &&
                    list.map((o) => {
                        const orderName = o?.name;
                        const customer = o?.customer || "-";
                        const amount = Number(o?.amount || 0);
                        const provider = String(o?.delivery_provider || "").trim();
                        const designated = String(o?.designated_number || "").trim();
                        const alertRemaining = getAlertRemaining(
                            o?.alert_time,
                            nowMs
                        );
                        const hasAlertTimer =
                            !!o?.alert_time && !!alertRemaining.label;
                        const isAlertDue =
                            hasAlertTimer && alertRemaining.isDue;
                        const kind = provider
                            ? t("callCenterOrders.kindDelivery")
                            : designated
                              ? t("callCenterOrders.kindInternal")
                              : t("callCenterOrders.kindExternal");
                        return (
                            <Box
                                key={orderName}
                                sx={{
                                    mb: 1,
                                    p: hasAlertTimer ? 1 : 0,
                                    borderRadius: 1,
                                    border: hasAlertTimer
                                        ? "1px solid"
                                        : "1px solid transparent",
                                    borderColor: isAlertDue
                                        ? "#e14954"
                                        : "rgba(46, 125, 50, 0.35)",
                                    backgroundColor: isAlertDue
                                        ? "rgba(225, 73, 84, 0.04)"
                                        : hasAlertTimer
                                          ? "rgba(46, 125, 50, 0.04)"
                                          : "transparent",
                                }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    gap={1}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle2"
                                            noWrap
                                            title={orderName}
                                        >
                                            {orderName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {customer} • {kind} • ₪
                                            {amount.toFixed(2)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            {t("callCenterOrders.owner")}:{" "}
                                            {o?.owner || "-"} •{" "}
                                            {dateTimeFormat(o?.modified)}
                                        </Typography>
                                        {hasAlertTimer ? (
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                sx={{
                                                    mt: 0.5,
                                                    fontWeight: 700,
                                                    color: isAlertDue
                                                        ? "#e14954"
                                                        : "#2e7d32",
                                                }}
                                            >
                                                {t("draftAlerts.countdown", {
                                                    time: alertRemaining.label,
                                                    defaultValue:
                                                        "Alert in {{time}}",
                                                })}
                                                {isAlertDue
                                                    ? ` • ${t("draftAlerts.due", {
                                                          defaultValue: "Due",
                                                      })}`
                                                    : ""}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>

                                <Box display="flex" gap={1} mt={1}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleOpenOrder(orderName)}
                                    >
                                        {t("callCenterOrders.open")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handlePrintOrder(orderName)}
                                        disabled={printOrder.isLoading}
                                    >
                                        {t("callCenterOrders.printOrder")}
                                    </Button>
                                </Box>

                                <Divider sx={{ mt: 1.5 }} />
                            </Box>
                        );
                    })}
            </Box>
        </Drawer>
    );
};

export default CallCenterOrdersDrawer;

